import React, { useEffect, useMemo, useState } from "react";


import SbModal from "uiCore/SbModal/SbModal";
import SbInput from "uiCore/SbInput/SbInput";
import SbForm from "uiCore/SbForm/SbForm";
import SbNotification from "uiCore/SbNotification/SbNotification";
import { fetchOrgStructure, getJdTemplate } from "Api/apiService";
import { FullWidthSelectStyled } from "./JdTemplates.styles";

// ─────────────────────────────────────────────────────────────────────────────

const CreateTemplateModal = ({ onClose, onSubmit, templates = [] }) => {
  const [form] = SbForm.useForm();
  const [orgStructure, setOrgStructure] = useState(null);
  const [extraCountries, setExtraCountries] = useState([]);
  const legalEntity      = SbForm.useWatch("legalEntity", form);
  const sourceTemplateId = SbForm.useWatch("sourceTemplateId", form);
  const [selectedSourceMarkdown, setSelectedSourceMarkdown] = useState("");

  useEffect(() => {
    let cancelled = false;
    const loadOrgStructure = async () => {
      try {
        const resp = await fetchOrgStructure();
        if (!cancelled) setOrgStructure(resp);
      } catch {
        if (!cancelled) setOrgStructure(null);
      }
    };
    loadOrgStructure();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (legalEntity) form.setFieldValue("country", undefined);
  }, [legalEntity, form]);

  const sourceTemplateOptions = useMemo(() => {
    if (!Array.isArray(templates)) return [];
    return templates.map((t) => ({
      value: t.sb_template_unique_id,
      label: t.org_jd_template_name ?? "—",
    }));
  }, [templates]);


  useEffect(() => {
    if (!sourceTemplateId) {
      setSelectedSourceMarkdown("");
      return;
    }
    let cancelled = false;
    const fetchTemplate = async () => {
      try {
        const data = await getJdTemplate(sourceTemplateId);
        if (!cancelled) setSelectedSourceMarkdown(data?.org_jd_template_md ?? "");
      } catch {
        if (!cancelled) setSelectedSourceMarkdown("");
      }
    };
    fetchTemplate();
    return () => { cancelled = true; };
  }, [sourceTemplateId]);

  const legalEntityOptions = useMemo(() => {
    const arr = orgStructure?.organization_structure;
    if (!Array.isArray(arr)) return [];
    return arr
      .map((item) => item?.legal_entity)
      .filter(Boolean)
      .map((v) => ({ value: v, label: v }));
  }, [orgStructure]);

  const countryOptions = useMemo(() => {
    const arr = orgStructure?.organization_structure;
    const base = [];
    if (Array.isArray(arr)) {
      if (legalEntity) {
        const selected = arr.find((x) => x?.legal_entity === legalEntity);
        (selected?.locations ?? []).forEach((l) => {
          const val = l?.name;
          if (val && !base.includes(val)) base.push(val);
        });
      } else {
        arr.forEach((entity) => {
          (entity?.locations ?? []).forEach((l) => {
            const val = l?.name;
            if (val && !base.includes(val)) base.push(val);
          });
        });
      }
    }
    extraCountries.forEach((v) => {
      if (v && !base.includes(v)) base.push(v);
    });
    return base.map((v) => ({ value: v, label: v }));
  }, [orgStructure, legalEntity, extraCountries]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onSubmit({ ...values, sourceMarkdown: selectedSourceMarkdown });
      form.resetFields();
      setExtraCountries([]);
    } catch (error) {
      // Inline form validation already highlights the missing required fields.
      console.error("Template form validation failed:", error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <SbModal
      open={true}
      title="New Template"
      onCancel={handleCancel}
      onOk={handleOk}
      okText="Create"
      cancelText="Cancel"
      destroyOnClose
    >
      <SbForm form={form} layout="vertical">

        <SbForm.Item
          label="Template Name"
          name="templateName"
          rules={[{ required: true, message: "Template name is required" }]}
        >
          <SbInput placeholder="e.g. Standard JD Template v1" />
        </SbForm.Item>

        <SbForm.Item
          label="Legal Entity"
          name="legalEntity"
          rules={[{ required: true, message: "Legal Entity is required" }]}
        >
          <FullWidthSelectStyled
            placeholder="Select legal entity"
            options={legalEntityOptions}
            showSearch
            optionFilterProp="label"
          />
        </SbForm.Item>

        <SbForm.Item
          label="Country"
          name="country"
          rules={[{ required: true, message: "Country is required" }]}
        >
          <FullWidthSelectStyled
            placeholder="Select country"
            options={countryOptions}
            showSearch
            optionFilterProp="label"
          />
        </SbForm.Item>
        
        <SbForm.Item
          label="Use Existing Template"
          name="sourceTemplateId"
          rules={[{ required: true, message: "Please select an existing template" }]}
        >
          <FullWidthSelectStyled
            placeholder={
              sourceTemplateOptions.length
                ? "Select a template to copy markdown from"
                : "No existing templates available"
            }
            options={sourceTemplateOptions}
            disabled={sourceTemplateOptions.length === 0}
            allowClear
            showSearch
            optionFilterProp="label"
          />
        </SbForm.Item>
      </SbForm>
    </SbModal>
  );
};

export default CreateTemplateModal;